import { PaperclipIcon } from "@phosphor-icons/react/dist/csr/Paperclip";
import { useQueryClient } from "@tanstack/react-query";
import { type Dispatch, type SetStateAction, useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { toast } from "@volca/ui/lib/toast";
import {
  ACCEPTED_FILE_TYPES,
  MAX_DOCUMENTS_PER_REPORT,
  MAX_DOCUMENT_BYTES,
} from "./document-constants";
import {
  reportKey,
  useDeleteReportDocument,
  useUploadReportDocuments,
} from "./query";
import type { ProfitabilityDocument } from "./types";
import { reportError } from "./utils";

export const useReportDocumentIds = (
  businessId: string,
  documentIds: string[],
  documents: ProfitabilityDocument[],
  onDocumentIdsChange: Dispatch<SetStateAction<string[]>>,
) => {
  const client = useQueryClient();
  const upload = useUploadReportDocuments(businessId);
  const remove = useDeleteReportDocument(businessId);

  const handleFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;
    if (documentIds.length + files.length > MAX_DOCUMENTS_PER_REPORT) {
      toast.error(
        `A report can read up to ${MAX_DOCUMENTS_PER_REPORT} documents`,
      );
      return;
    }
    const tooLarge = files.find((file) => file.size > MAX_DOCUMENT_BYTES);
    if (tooLarge) {
      toast.error(`${tooLarge.name} is larger than 25 MB`);
      return;
    }
    upload.mutate(files, {
      onSuccess: (uploaded) =>
        onDocumentIdsChange((current) => {
          const ids = [...current, ...uploaded.map((document) => document.id)];
          client.setQueryData(
            [...reportKey(businessId), "documents", ids],
            [
              ...documents.filter((document) => current.includes(document.id)),
              ...uploaded,
            ],
          );
          return ids;
        }),
      onError: (error) =>
        toast.error("The documents could not be added", {
          description: reportError(error),
        }),
    });
  };

  const removeDocument = (id: string) => {
    onDocumentIdsChange((current) => current.filter((other) => other !== id));
    remove.mutate(id);
  };

  return { handleFiles, removeDocument, uploading: upload.isPending };
};

export const ReportDocumentsPicker = ({
  uploading,
  onFiles,
}: {
  uploading: boolean;
  onFiles: (files: FileList | null) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        className="hidden"
        data-testid="report-documents-input"
        onChange={(event) => {
          onFiles(event.target.files);
          event.target.value = "";
        }}
      />
      <Button
        disabled={uploading}
        variant="secondary"
        aria-busy={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {<PaperclipIcon />}
        Add documents
      </Button>
    </>
  );
};
